// frontend/src/components/event/EventCard.js
import React from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { useAuth } from "../../contexts/AuthContext";
import {
  Calendar,
  Clock,
  Users,
  User,
  Lock,
  CheckCircle,
  Coins,
  PlayCircle,
} from "lucide-react";
import EventFormatBadge from "./EventFormatBadge";
import {
  getEventTimeDisplay,
  getEventLiveStatus,
} from "../../utils/eventTimeUtils";

const EventCard = ({ event, featured }) => {
  const { language, t } = useLanguage();
  const { hasAccess: checkAccess } = useAuth();
  const { isLive, isStartingSoon, minutesUntilStart } =
    getEventLiveStatus(event);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString(language === "de" ? "de-DE" : "en-US", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: event.timezone || "Europe/Zurich",
    });
  };

  const isPast = new Date(event.end_datetime) < new Date();
  const isUpcoming = new Date(event.start_datetime) > new Date();
  const isRegistered = event.participant?.status === "confirmed";
  const isWaitlisted = event.participant?.status === "waitlisted";
  const isInvited =
    event.participant?.source === "invitation" &&
    event.participant?.status === "pending";

  // Contract badge colors
  const contractColors = {
    Free: "bg-green-500",
    Registered: "bg-blue-500",
    Basic: "bg-[#23CEE7]",
    Premium: "bg-[#FC0058]",
    Pro: "bg-black",
    Invite: "bg-purple-600",
  };

  const timeDisplay = getEventTimeDisplay(event, language);

  return (
    <Link
      to={`/event/${event.slug}`}
      className={`group block bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 ${
        isLive
          ? "ring-2 ring-red-500 shadow-red-100"
          : isStartingSoon
          ? "ring-2 ring-orange-400 shadow-orange-100"
          : featured
          ? "ring-2 ring-[#EE005F]"
          : ""
      }`}
    >
      {/* Featured highlight bar */}
      {featured && (
        <div className="bg-[#EE005F] text-white text-xs font-bold text-center py-1 tracking-wide uppercase">
          {t({ de: "Highlight", en: "Highlight" })}
        </div>
      )}
      {/* Image Section */}
      <div className="relative aspect-video bg-gradient-to-br from-[#002855] to-[#00A3E0]">
        {event.imageUrl && (
          <img
            src={event.imageUrl}
            alt={event.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        )}

        {/* Date / Live Status Overlay */}
        {isLive ? (
          <div className="absolute bottom-2 left-2 bg-red-600 text-white px-3 py-1.5 rounded-md flex items-center gap-2 text-sm font-bold">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"></span>
            </span>
            LIVE
          </div>
        ) : isStartingSoon ? (
          <div className="absolute bottom-2 left-2 bg-orange-500 text-white px-3 py-1.5 rounded-md text-sm font-bold">
            {t({
              de: `Startet in ${minutesUntilStart} Min.`,
              en: `Starts in ${minutesUntilStart} min`,
            })}
          </div>
        ) : isPast && event.hasRecording ? (
          <div className="absolute bottom-2 left-2 bg-[#00A3E0] text-white px-3 py-1.5 rounded-md">
            <div className="flex items-center gap-2 text-sm font-medium">
              <PlayCircle className="w-4 h-4" />
              <span>{t({ de: "Aufzeichnung", en: "Recording" })}</span>
            </div>
          </div>
        ) : (
          <div className="absolute bottom-2 left-2 bg-black/70 text-white px-3 py-1.5 rounded-md">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(event.start_datetime)}</span>
            </div>
          </div>
        )}

        {/* Contract Badge */}
        <div
          className={`absolute top-2 right-2 ${
            contractColors[event.access_type] || "bg-gray-500"
          } text-white text-xs font-bold px-2 py-1 rounded`}
        >
          {event.access_type}
        </div>

        {/* Format Badge */}
        <div className="absolute top-2 left-2 flex flex-wrap gap-1 max-w-[65%]">
          <EventFormatBadge format={event.format} location={event.location} eventType={event.event_type} size="sm" />
        </div>

        {/* Lock overlay for inaccessible events */}
        {!event.hasAccess && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <Lock className="w-8 h-8 text-white/80" />
          </div>
        )}

        {/* Registered indicator */}
        {isRegistered && (
          <div className="absolute bottom-2 right-2 bg-green-500 text-white px-2 py-1 rounded-md flex items-center gap-1 text-xs">
            <CheckCircle className="w-3 h-3" />
            {t({ de: "Angemeldet", en: "Registered" })}
          </div>
        )}

        {/* Invited indicator */}
        {isInvited && (
          <div className="absolute bottom-2 right-2 bg-purple-500 text-white px-2 py-1 rounded-md flex items-center gap-1 text-xs">
            {t({ de: "Eingeladen", en: "Invited" })}
          </div>
        )}

        {/* Waitlisted indicator */}
        {isWaitlisted && (
          <div className="absolute bottom-2 right-2 bg-amber-500 text-white px-2 py-1 rounded-md flex items-center gap-1 text-xs">
            <Clock className="w-3 h-3" />
            {t({ de: "Warteliste", en: "Waitlisted" })}
          </div>
        )}
      </div>

      {/* Content Section */}
      <div className="p-4">
        {/* Title */}
        <h3 className="font-bold text-[#002855] group-hover:text-[#00A3E0] transition-colors line-clamp-2 mb-2">
          {event.subtitle || event.title}
        </h3>

        {/* Abstract */}
        {event.abstract && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-3">
            {event.abstract}
          </p>
        )}

        {/* Time & Speaker row */}
        <div className="flex items-start justify-between gap-2 text-sm text-gray-500 mb-2">
          <div className="flex items-start gap-1.5 flex-shrink-0">
            <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <span>
                {timeDisplay.eventTimeLine.start} -{" "}
                {timeDisplay.eventTimeLine.end}{" "}
                <span className="text-xs text-gray-400">
                  {timeDisplay.eventTimeLine.abbreviation}
                </span>
              </span>
              {timeDisplay.showLocalTime && timeDisplay.localTimeLine && (
                <div className="text-xs text-gray-400">
                  {timeDisplay.localTimeLine.start} -{" "}
                  {timeDisplay.localTimeLine.end}{" "}
                  {timeDisplay.localTimeLine.abbreviation}{" "}
                  ({timeDisplay.localTimeLine.label})
                </div>
              )}
            </div>
          </div>
          {event.speaker_name && (
            <div className="flex items-center gap-1 min-w-0 text-gray-500">
              <User className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">{event.speaker_name}</span>
            </div>
          )}
        </div>

        {/* Spots Remaining (if limited) */}
        {event.spotsRemaining &&
          (event.spotsRemaining.online !== null ||
            event.spotsRemaining.in_person !== null) && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Users className="w-4 h-4" />
              <span>
                {event.format === "hybrid" ? (
                  <>
                    {event.spotsRemaining.online !== null && (
                      <span>
                        {event.spotsRemaining.online}{" "}
                        {t({ de: "Online-Plätze", en: "online spots" })}
                      </span>
                    )}
                    {event.spotsRemaining.online !== null &&
                      event.spotsRemaining.in_person !== null && " | "}
                    {event.spotsRemaining.in_person !== null && (
                      <span>
                        {event.spotsRemaining.in_person}{" "}
                        {t({ de: "Vor-Ort-Plätze", en: "in-person spots" })}
                      </span>
                    )}
                  </>
                ) : event.spotsRemaining.online !== null ? (
                  <span>
                    {event.spotsRemaining.online}{" "}
                    {t({ de: "Online-Plätze", en: "online spots" })}
                  </span>
                ) : event.spotsRemaining.in_person !== null ? (
                  <span>
                    {event.spotsRemaining.in_person}{" "}
                    {t({ de: "Vor-Ort-Plätze", en: "in-person spots" })}
                  </span>
                ) : null}
              </span>
            </div>
          )}

        {/* Pricing */}
        {event.pricing_type === "donation" && (
          <div className="mt-2 flex items-center gap-1.5 text-sm text-gray-600">
            <Coins className="w-4 h-4" />
            {t({ de: "Kollekte", en: "Donation" })}
            {event.suggested_donation && (
              <span>: {event.donation_currency} {event.suggested_donation}</span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
};

export default EventCard;
